/**
 * INC-349 / DEV-12867: fail-open watchdog for the Evidence readiness gate.
 *
 * `sourceControlReadinessAtom` defaults to `pending` and the composer is
 * disabled until it turns `ready`. INC-349 happened because entire auth
 * branches (Epic FHIR, desktop Tauri, on-prem Keycloak) shipped without a
 * readiness writer, leaving the composer silently disabled for the whole
 * session. The writer is now a single unconditional mount in `AuthWrapper` and
 * the branches only publish their auth state into it, so the shape of that
 * regression has moved: a branch cannot lack a writer any more, but it can
 * mount a shell that never publishes, which pins readiness to `pending` just
 * the same. That is what the error tier below watches.
 *
 * This hook is the structural safety net: if readiness stays `pending` past
 * the shared deadline, it fails OPEN — the composer enables, and any send made
 * under fail-open uses `SOURCE_CONTROL_DEFAULTS` rather than the cached toggle
 * (which is not user-scoped; see the payload substitution in `useChatStream`)
 * — and reports the stall so it is never silent. It deliberately does nothing
 * for `blocked`, which is an explicit error state with its own banner + retry
 * UX in Chat.
 *
 * Mount once at the TOP of `AuthWrapper`, before any auth branch returns and
 * above the workspace-hydration gate, beside `useSourceControlReadinessWriter`
 * — the two halves of the gate share a lifetime. Keep it free of feature
 * flags: a kill switch on the safety net recreates the INC-349 trap where the
 * escape hatch lived inside the code path that failed to mount.
 */
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useEffect } from 'react';

import {
  SOURCE_CONTROL_READINESS_TIMEOUT_MS,
  SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING,
  sourceControlReadinessAtom,
  sourceControlSyncLifecycleAtom,
  sourceControlWriterMountedAtom,
} from '@/atoms/sourceControlAtoms';
import { getEvidenceFailOpenTimeoutOverrideMs } from '@/features/flags';
import { logger } from '@/utils/logger/logger';
import { addDatadogRumAction, addDatadogRumError } from '@/utils/tracing';

export type SourceControlFailOpenSurface = 'watchdog' | 'send-path';

/**
 * Clamp bounds for the remote deadline override. The floor keeps a
 * fat-fingered low value from routing every send onto default sources during
 * ordinary hydration (the gating user fetch alone has p99 ≈ 4.3s); the
 * ceiling keeps a high value from effectively disabling the net — fail-open
 * always fires within a minute. Together they make the override a tuner,
 * never a kill switch (the INC-349 constraint).
 */
const FAIL_OPEN_TIMEOUT_FLOOR_MS = 5_000;
const FAIL_OPEN_TIMEOUT_CEILING_MS = 60_000;

/**
 * Effective fail-open deadline: the remote override (Flagsmith/OpenFeature)
 * clamped to the bounds above, falling back to the baked
 * `SOURCE_CONTROL_READINESS_TIMEOUT_MS` when the flag is absent, the
 * provider has not reconciled yet, or the value is not a finite number.
 */
export function getSourceControlFailOpenTimeoutMs(): number {
  const override = getEvidenceFailOpenTimeoutOverrideMs(
    SOURCE_CONTROL_READINESS_TIMEOUT_MS
  );
  if (!Number.isFinite(override)) return SOURCE_CONTROL_READINESS_TIMEOUT_MS;
  return Math.min(
    Math.max(override, FAIL_OPEN_TIMEOUT_FLOOR_MS),
    FAIL_OPEN_TIMEOUT_CEILING_MS
  );
}

const NO_LIFECYCLE_MESSAGE =
  'Source control readiness has no published auth lifecycle; failing open';
const SLOW_HYDRATION_MESSAGE = 'Source control hydration exceeded the fail-open deadline';

/**
 * The state the two telemetry tiers are split on, read at fire time.
 * `writerMounted` is carried for continuity of the RUM context (it is true on
 * every path now that the writer is unconditional); `lifecyclePublished` is
 * the discriminator.
 */
export interface SourceControlFailOpenState {
  writerMounted: boolean;
  lifecyclePublished: boolean;
}

/**
 * Two-tier fail-open telemetry, split on whether any auth shell has published
 * a lifecycle — the identity of `sourceControlSyncLifecycleAtom`, which still
 * holds `SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING` until one does. The split used to key
 * on `writerMounted`; the writer is unconditional now and that flag is always
 * true here, so the discriminator moved to the signal that still varies at the
 * same moment the old one did.
 *
 * - Nothing published: no auth branch has reported in. INC-349's regression in
 *   its current shape is one cause — a branch shipping without
 *   `usePublishSourceControlSyncLifecycle`, which pins readiness to `pending`
 *   for that whole surface, permanently. RUM error with a stable, dedupable
 *   fingerprint; firing once per session on the broken branch is the alarm, by
 *   design.
 * - Published: a transient hydration stall (auth/user query past the deadline
 *   — user-endpoint p99 is ~4.3s, so a >10s tail exists). Warn + countable RUM
 *   action, NOT an error, so slow sessions cannot flood the incident
 *   fingerprint or fatigue its alert.
 */
export function reportSourceControlFailOpen(
  { writerMounted, lifecyclePublished }: SourceControlFailOpenState,
  surface: SourceControlFailOpenSurface,
  timeoutMs: number
): void {
  const context = {
    feature: 'evidence-source-control',
    surface,
    writerMounted,
    lifecyclePublished,
    timeoutMs,
  };
  if (lifecyclePublished) {
    logger.warn(SLOW_HYDRATION_MESSAGE, context);
    addDatadogRumAction('source_control_readiness_fail_open_stall', context);
    return;
  }
  logger.error(NO_LIFECYCLE_MESSAGE, context);
  addDatadogRumError(new Error(NO_LIFECYCLE_MESSAGE), context);
}

export function useSourceControlReadinessFailOpen(): void {
  const readiness = useAtomValue(sourceControlReadinessAtom);
  const setReadiness = useSetAtom(sourceControlReadinessAtom);
  const store = useStore();
  // Keyed on the status boolean, not the atom value: redundant `pending`
  // rewrites must not reset the deadline — it counts from when the gate
  // entered `pending`, and re-arms only after readiness actually left it.
  const isPending = readiness.status === 'pending';

  useEffect(() => {
    if (!isPending) return undefined;

    // Resolved at arm time so a Flagsmith retune applies to the next pending
    // window without a redeploy (clamped — see getSourceControlFailOpenTimeoutMs).
    const timeoutMs = getSourceControlFailOpenTimeoutMs();
    const timeoutId = setTimeout(() => {
      setReadiness({ status: 'ready', source: 'fail-open' });
      // Read at fire time, unsubscribed: these flags only matter for
      // classifying THIS event, and subscribing would re-run the effect.
      reportSourceControlFailOpen(
        {
          writerMounted: store.get(sourceControlWriterMountedAtom),
          lifecyclePublished:
            store.get(sourceControlSyncLifecycleAtom) !==
            SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING,
        },
        'watchdog',
        timeoutMs
      );
    }, timeoutMs);

    return () => clearTimeout(timeoutId);
  }, [isPending, setReadiness, store]);
}
